/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect, useRef, useContext, useState, useCallback } from 'react';
import Upload from '../../../icons/Upload.svg?react';
import UploadCloud from '../../../icons/UploadCloud.svg?react';
import MikeButton from '../../mike-button';
import './ImportMeshPanel.css';
import Api from '../../../apis/meshApi';
import { GlobalContext } from '../../../store/Store';
import { IGlobalContext } from '../../app/AppWrapper';
import ActionType from '../../../store/ActionType';
import PlatformImportProgress from './PlatformImportProgress';
import {
  SPINNER_TIMEOUT,
  IProgressItem,
} from '../../mike-topbar-progress-spinner';
import MikeProjectExplorerDialog from '../../mike-project-explorer/MikeProjectExplorerDialog';
import { getProjectContent } from '../../app/apiFunctions';
import {
  setProjectContentPage,
  setProjectContentOrder,
  setProjectContentOrderBy,
  setProjectContentRowsPerPage,
} from '../../../store/projectAction';
import {
  IGetProject,
  IGetCustomProject,
  IGetProjectPath
} from '../../../Models/IGetProject';
import { IGetDataset } from '../../../Models/IGetDataset';
import useLocalStorage from '../../app/Polling/PollingStorage';
import { getSortedPageRows } from '../../TableComponents/support';
import { customProjectContentColumns } from '../../mike-project-explorer/MikeProjectContentTable/projectContentColumns';
import { BlobServiceClient  } from "@azure/storage-blob";

const containerStyle = css`
  display: flex;
`;
const CAPABILITIES = 'capabilities';
const CANREAD = 'canRead';

// To calculate Mesh uploading progress as a total of uploading and converting time
const BROWSER_STEP = 0; // a number between 0 and 100 %


function ImportMeshPanel() {
  const [globalState, globalDispatch] = useContext(GlobalContext);
  const globalContext: IGlobalContext = { state: globalState, dispatch: globalDispatch };
  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [, setPollingSettings] = useLocalStorage('polling-settings');

  const currentNavigationProject =
    globalState.projectContent && globalState.projectContent.currentNavigationProject;
  const projectContent = globalState.projectContent && globalState.projectContent.projectContent;
  const currentNavigationProjectPath =
    globalState.projectContent && globalState.projectContent.currentNavigationProjectPath;
  const order = globalState.projectContent ? globalState.projectContent.order : 'desc';
  const orderBy = globalState.projectContent && globalState.projectContent.orderBy;
  const page = globalState.projectContent && globalState.projectContent.page;
  const rowsPerPage = globalState.projectContent && globalState.projectContent.rowsPerPage;
  const loadingProjectContent =
    globalState.projectContent && globalState.projectContent.loadingProjectContent;
  const [pageRows, setPageRows] = useState(Array<IGetCustomProject | IGetDataset>());
  const [meshesToImport, setMeshesToImport] = useState([]);
  const currentProject = globalState.project && globalState.project.currentProject;

  const canEdit = currentProject && currentProject.canEdit;

  const openProjectExplorer = async () => {
    globalDispatch(setProjectContentPage(0));
    globalDispatch(setProjectContentOrder('desc'));
    globalDispatch(setProjectContentOrderBy(''));
    setOpen(true);
    await getProjectContent({
      projectID: globalState.project.currentProject.id,
      globalContext: globalContext,
    });
  };

  const onItemClick = async item => {
    if (CANREAD in item) {
      const projectItem: IGetCustomProject = item as IGetCustomProject;
      if (projectItem.canRead) {
        await getProjectContent({ projectID: item.id, globalContext: globalContext });
      }
      globalDispatch(setProjectContentPage(0));
      globalDispatch(setProjectContentOrder('desc'));
      globalDispatch(setProjectContentOrderBy(''));
    } else if (CAPABILITIES in item) {
      const projectItem: IGetProjectPath = item as IGetProjectPath;
      if (projectItem.capabilities.canListContent) {
        await getProjectContent({ projectID: item.id, globalContext: globalContext });
      }
      globalDispatch(setProjectContentOrder('desc'));
      globalDispatch(setProjectContentOrderBy(''));
    }
  };

  const onSelecteditemsChanged = useCallback(
    (selectedItems: (IGetProject | IGetCustomProject | IGetDataset)[]) => {
      setSelectedItems(selectedItems);
    },
    []
  );

  const onCancel = () => {
    setOpen(false);
  };
  const onOk = () => {
    if (selectedItems.length > 0) {
      setMeshesToImport(selectedItems);
    }
    setOpen(false);
  };

  const onHandleChangePage = useCallback(
    (page: number) => {
      globalDispatch(setProjectContentPage(page));
    },
    [globalDispatch]
  );

  const onHandleChangeRowsPerPage = useCallback(
    (rowsPerPage: number) => {
      globalDispatch(setProjectContentRowsPerPage(rowsPerPage));
    },
    [globalDispatch]
  );

  const onHandleRequestSort = useCallback(
    (orderBy: string | ((item: any) => string | number), order: 'asc' | 'desc') => {
      globalDispatch(setProjectContentPage(0));
      globalDispatch(setProjectContentOrder(order));
      globalDispatch(setProjectContentOrderBy(orderBy));
    },
    [globalDispatch]
  );

  useEffect(() => {
    setPageRows(getSortedPageRows(projectContent, orderBy, order, page, rowsPerPage));
  }, [projectContent, orderBy, order, page, rowsPerPage]);

  useEffect(() => {
    if (open) {
      setSelectedItems(Array<IGetProject | IGetDataset>());
    }
  }, [open]);

  const fileselect = useRef(null);
  const filedrag = useRef(null);
  const progressItemRef = useRef<IProgressItem>();

  const uploadMesh = function(e) {
    if (e.target.length > 0) {
      const files = e.target[0].files;
      doUploadMesh(files);
    }
  };

  const doUploadMesh = function(files: File[]) {
    if (files.length > 0) {
      const fn = files[0].name;
      progressItemRef.current = {
        title: fn,
        progressValue: 0,
        id: fn,
        browserStep: BROWSER_STEP,
      };
      globalDispatch({
        type: ActionType.ADD_POLLING_ITEM,
        data: progressItemRef.current,
      });
      doUploadRequest(files[0]);
    }
  };

  const doUploadRequest = async function(file: File) {
    try {
      const response = await Api.uploadRequest(globalState.project.currentProject.id, file.name);
      
      if (response.data && response.data.Status === 400) {
        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: progressItemRef.current,
        });
        progressItemRef.current = undefined;
        globalDispatch({
          type: ActionType.SET_MESSAGE,
          message: response.data.ValidationErrors[0].Description,
        });
        globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
        globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
        fileselect.current.value = null;
      } else {
        progressItemRef.current = {
          title: file.name,
          progressValue: 0,
          id: file.name,
          browserStep: BROWSER_STEP,
        };
        fileselect.current.value = null;   
        // ToDo: migrate to newer dependency     
        processAzureBlobServiceUpload(response.data, file);
      }
    } catch (e) {
      console.log('error catch: ', e.message);
      globalDispatch({
        type: ActionType.SET_MESSAGE,
        message: `Error uploading file: ${e.message}`,
      });
      globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
      globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });

      globalDispatch({
        type: ActionType.ADDUP_POLLING_ITEM,
        data: { ...progressItemRef.current, error: true },
      });
      setTimeout(() => {
        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: progressItemRef.current,
        });
        progressItemRef.current = undefined;
        fileselect.current.value = null;
      }, SPINNER_TIMEOUT);
    }
  };

  const processAzureBlobServiceUpload = async function(request, file: File) {
    const uploadHost = request.uploadHost
    const sas = request.sasToken
    const blobServiceClient = new BlobServiceClient(`https://${uploadHost}${sas}`);


    const containerName = request.uploadContainer
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(file.name)
    const handleProgress = (loadedBytes) => {    
      const progress = Number(((loadedBytes.loadedBytes / file.size) * 100).toFixed(1))
      progressItemRef.current.progressValue = Math.round(progress / 2);
      globalDispatch({
        type: ActionType.UPDATE_POLLING_ITEM,
        data: progressItemRef.current,
      });
    }
    try{    
      const blockSize = file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
      await blockBlobClient.uploadData(file, {maxSingleShotSize: blockSize, onProgress: handleProgress});   
      Api.uploadCompleted(globalState.project.currentProject.id, file.name).then(response => {
        const boundaryItem: IProgressItem = {
          title: `Starting boundary extraction`,
          id: response.data.boundaryExtractionJobGuid,
          progressValue: 0,
          browserStep: 0,
        };
        const meshTilingItem: IProgressItem = {
          title: `Starting mesh tiling extraction`,
          id: response.data.meshTilingJobGuid,
          progressValue: 0,
          browserStep: 0,
        };
        const meshTilingOrgItem: IProgressItem = {
          title: `Starting mesh tiling org. extraction`,
          id: response.data.meshTilingOriginalJobGuid,
          progressValue: 0,
          browserStep: 0,
        };

        globalDispatch({
          type: ActionType.SET_POLLING_UPDATE,
          data: response.data.boundaryExtractionJobGuid,
        });
        globalDispatch({
          type: ActionType.SET_POLLING_UPDATE,
          data: response.data.meshTilingJobGuid,
        });
        globalDispatch({
          type: ActionType.SET_POLLING_UPDATE,
          data: response.data.meshTilingOriginalJobGuid,
        });

        globalDispatch({
          type: ActionType.ADD_POLLING_ITEM,
          data: boundaryItem,
        });
        globalDispatch({
          type: ActionType.ADD_POLLING_ITEM,
          data: meshTilingItem,
        });
        globalDispatch({
          type: ActionType.ADD_POLLING_ITEM,
          data: meshTilingOrgItem,
        });

        setPollingSettings([
          ...globalState.pollingIds,
          response.data.boundaryExtractionJobGuid,
          response.data.meshTilingJobGuid,
          response.data.meshTilingOriginalJobGuid,
        ]);

        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: { ...progressItemRef.current },
        });
      });
    }
    catch(error){
      console.log(error)
      globalDispatch({
        type: ActionType.SET_MESSAGE,
        message: `Error uploading file: ${JSON.stringify(error)}`,
      });
      globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
      globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });

      globalDispatch({
        type: ActionType.ADDUP_POLLING_ITEM,
        data: { ...progressItemRef.current, error: true },
      });
      setTimeout(() => {
        globalDispatch({
          type: ActionType.RM_POLLING_ITEM,
          data: progressItemRef.current,
        });
        progressItemRef.current = undefined;
        fileselect.current.value = null;
      }, SPINNER_TIMEOUT);
    }
  } 

// Migrated from @azure-storage to @azure/blob-storage
/*   const processAzureBlobServiceUpload = function(request, file) {
    
    const fileService = AzureBlobService.createBlobServiceWithSas(
      request.uploadHost,
      request.sasToken
    ); 

    const blockSize = file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
    fileService.singleBlobPutThresholdInBytes = blockSize;
    const options = { blockSize };

    const speedSummary = fileService.createBlockBlobFromBrowserFile(
      request.uploadContainer,
      request.meshName,
      file,
      options,
*/

  // file selection part ---------------------------------------------------------------------------------------------------------------------------------- /

  const FileSelectHandler = function(e) {
    // cancel event and hover styling
    FileDragExit(e);
    const files = e.target.files || e.dataTransfer.files;
    // process all File objects
    if (!canEdit) {
      globalDispatch({
        type: ActionType.SET_MESSAGE,
        message: 'You do not have write access to current projects',
      });
      globalDispatch({ type: ActionType.SET_MESSAGE_TYPE, messagetype: 'error' });
      globalDispatch({ type: ActionType.SET_SHOW_MESSAGE, show: true });
    } else {
      doUploadMesh(files);
    }
  };

  // file drag hover and enter
  const FileDragEnter = function(e) {
    e.stopPropagation();
    e.preventDefault();
    filedrag.current.classList.add('hover');
  };

  const FileDragExit = function(e) {
    e.stopPropagation();
    e.preventDefault();
    filedrag.current.classList.remove('hover');
  };

  useEffect(() => { 
    if ((globalState.map as any).olMap && currentProject) {
      // file select
      fileselect.current.removeEventListener('change', FileSelectHandler, false);
      fileselect.current.addEventListener('change', FileSelectHandler, false);

      // is XHR2 available?
      const xhr = new XMLHttpRequest();
      if (xhr.upload) {
        // file drop
        filedrag.current.removeEventListener('dragenter', FileDragEnter, false);
        filedrag.current.removeEventListener('dragover', FileDragEnter, false);
        filedrag.current.removeEventListener('dragleave', FileDragExit, false);
        filedrag.current.removeEventListener('drop', FileSelectHandler, false);

        filedrag.current.addEventListener('dragenter', FileDragEnter, false);
        filedrag.current.addEventListener('dragover', FileDragEnter, false);
        filedrag.current.addEventListener('dragleave', FileDragExit, false);
        filedrag.current.addEventListener('drop', FileSelectHandler, false);
      }
    }    
  }, [(globalState.map as any).olMap, currentProject]);
  return (
    <>
      <div className="menuContainer">
        <form encType="multipart/form-data" onSubmit={uploadMesh}>
          <div id="filedrag" ref={filedrag}>
            <Upload /> Drop mesh files here...{' '}
          </div>
          <div className="buttonsContainer">
            <div>
              {/* <Tooltip
                title={
                  canEdit
                    ? 'Upload and convert mesh from local disc'
                    : 'You do not have write access to current project'
                }> */}
                <>
                  <input
                    disabled={!canEdit}
                    type="file"
                    id="file"
                    ref={fileselect}
                    name="files"
                    accept=".mesh, .Mesh"
                  />
                  <label className="buttonFakeLook" htmlFor="file">
                    <div className="buttonContentWrapper">
                      <div className="buttonIconWrapper">
                        <Upload />
                      </div>
                      <div className="buttonTextWrapper">Upload</div>
                    </div>
                  </label>
                </>
              {/* </Tooltip> */}
              <div className="descriptionFooter buttonText">From local computer</div>
            </div>

            <div>
              {/* <Tooltip
                title={
                  canEdit
                    ? 'Import mesh from platform folder in MIKE Data Admin'
                    : 'You do not have write access to current project'
                }> */}
                <MikeButton variant="contained" onClick={openProjectExplorer} disabled={!canEdit}>
                  <div className="buttonContentWrapper">
                    <div className="buttonIconWrapper">
                      <UploadCloud />
                    </div>
                    <div className="buttonTextWrapper">Import</div>
                  </div>
                </MikeButton>
              {/* </Tooltip> */}
              <div className="descriptionFooter buttonText">From cloud Platform</div>
            </div>
          </div>

          <div id="submitbutton">
            <input type="submit" value="Upload" />
          </div>
        </form>
        <PlatformImportProgress meshesToImport={meshesToImport} />
      </div>

      <div css={containerStyle}>
        <MikeProjectExplorerDialog
          columns={customProjectContentColumns}
          dialogTitle="Select mesh-file(s) to be imported"
          okButtonDisabled={false}
          okButtonLabel={
            selectedItems.length === 0 ? 'Import' : 'Import (' + selectedItems.length + ')'
          }
          onCancel={onCancel}
          onOk={onOk}
          onChangePage={onHandleChangePage}
          onChangeRowsPerPage={onHandleChangeRowsPerPage}
          onHandleRequestSort={onHandleRequestSort}
          onItemClick={onItemClick}
          onSelectionChange={onSelecteditemsChanged}
          order={order}
          orderBy={orderBy}
          selectedItems={selectedItems}
          open={open}
          loading={loadingProjectContent}
          page={page}
          pagination={projectContent.length > 10}
          project={currentNavigationProject !== null ? currentNavigationProject : undefined}
          projectContent={pageRows}
          projectPath={currentNavigationProjectPath}
          rowsPerPage={rowsPerPage}
          selectable={true}
          totalCount={projectContent.length}
        />
      </div>
    </>
  );
}

export default React.memo(ImportMeshPanel);
